/**
 * Small inline SVG glyphs, standing in for the plain Unicode arrows/chevrons
 * UI chrome (not data) previously used — those render with inconsistent
 * stroke weight across platforms/fonts, unlike every other line in the
 * design system. All use `currentColor` so they inherit their container's
 * text color, and default to 2px strokes to match Layout's own MenuIcon.
 */
import type { SVGProps } from 'react';

type IconProps = Omit<SVGProps<SVGSVGElement>, 'viewBox' | 'aria-hidden' | 'focusable'>;

function BaseIcon({ width = 16, height = 16, ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={width}
      height={height}
      aria-hidden="true"
      focusable="false"
      fill="none"
      {...rest}
    />
  );
}

export function ArrowDownIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path
        d="M12 4 V20 M6 14 L12 20 L18 14"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </BaseIcon>
  );
}

export function ArrowUpIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path
        d="M12 20 V4 M6 10 L12 4 L18 10"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </BaseIcon>
  );
}

export function ChevronDownIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path
        d="M5 9 L12 16 L19 9"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </BaseIcon>
  );
}

export function ChevronRightIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path
        d="M9 5 L16 12 L9 19"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </BaseIcon>
  );
}

export function ChevronLeftIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path
        d="M15 5 L8 12 L15 19"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </BaseIcon>
  );
}

export function ExternalLinkIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path
        d="M9 6 H18 V15 M18 6 L7 17"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </BaseIcon>
  );
}

/* Navigation glyphs for the phone tab bar — 1.75px strokes, round caps. */
function NavIcon({ d, ...props }: IconProps & { d: string }) {
  return (
    <BaseIcon width={22} height={22} {...props}>
      <path
        d={d}
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </BaseIcon>
  );
}

/** Dashboard: a wallet. */
export function WalletIcon(props: IconProps) {
  return (
    <NavIcon
      d="M4 7.5 A2.5 2.5 0 0 1 6.5 5 H18 V8 M4 7.5 V17.5 A1.5 1.5 0 0 0 5.5 19 H20 V8 H6.5 A2.5 2.5 0 0 1 4 7.5 Z M16 13.5 H16.5"
      {...props}
    />
  );
}

/** History: a list with a clock hand. */
export function HistoryIcon(props: IconProps) {
  return <NavIcon d="M4 12 A8 8 0 1 0 6.3 6.3 M4 4 V7 H7 M12 8 V12 L15 14" {...props} />;
}

/** 15m Predictor: a stopwatch. */
export function TimerIcon(props: IconProps) {
  return (
    <NavIcon
      d="M12 21 A7.5 7.5 0 1 0 12 6 A7.5 7.5 0 0 0 12 21 Z M12 9.5 V13.5 M10 3 H14 M18.5 6.5 L19.75 5.25"
      {...props}
    />
  );
}

/** Fight research: two facing chevrons. */
export function VersusIcon(props: IconProps) {
  return <NavIcon d="M4 6 L10 12 L4 18 M20 6 L14 12 L20 18" {...props} />;
}

/** Crash Bounce: a price line that drops hard, then turns back up. */
export function CrashIcon(props: IconProps) {
  return (
    <NavIcon d="M3 6 L8 9 L12 18 L16 13 L21 15 M17.5 15.5 L21 15 L19.5 11.8" {...props} />
  );
}

/** Market Maker: a bid and an ask resting either side of a spread. */
export function SpreadIcon(props: IconProps) {
  return (
    <NavIcon
      d="M3 9 H10 M7 6 L10 9 L7 12 M21 15 H14 M17 12 L14 15 L17 18 M12 4 V20"
      {...props}
    />
  );
}

/** Liquidity Rewards: a coin resting on a shelf (an order paid to wait). */
export function RewardIcon(props: IconProps) {
  return (
    <NavIcon d="M4 18 H20 M12 6 A5 5 0 1 0 12.01 6 M12 9 V13 M10 11 H14" {...props} />
  );
}

/** Martingale: stakes doubling, a staircase of bars. */
export function DoublingIcon(props: IconProps) {
  return <NavIcon d="M4 20 H20 M6 20 V18 M10 20 V15 M14 20 V10 M18 20 V4" {...props} />;
}

/** Breakout Watch: a flat line breaking up out of a box. */
export function BreakoutIcon(props: IconProps) {
  return (
    <NavIcon
      d="M3 15 H12 L16 7 L21 4 M17.5 3.6 L21 4 L20.4 7.5 M3 11 H13 M3 19 H13"
      {...props}
    />
  );
}

/** Listing radar: a radar sweep. */
export function RadarIcon(props: IconProps) {
  return (
    <NavIcon
      d="M12 12 L18.5 5.5 M12 3 A9 9 0 1 0 21 12 M12 7 A5 5 0 1 0 17 12 M12 11 A1 1 0 1 0 13 12"
      {...props}
    />
  );
}

/** Paper tests: a lab flask. */
export function FlaskIcon(props: IconProps) {
  return (
    <NavIcon
      d="M9 3 H15 M10 3 V9.5 L4.6 18.4 A1.4 1.4 0 0 0 5.8 20.5 H18.2 A1.4 1.4 0 0 0 19.4 18.4 L14 9.5 V3 M7.2 15 H16.8"
      {...props}
    />
  );
}

/** Rug Check: a shield with a check mark. */
export function ShieldIcon(props: IconProps) {
  return (
    <NavIcon
      d="M12 3 L19 6 V11.5 C19 15.6 16.1 19.2 12 21 C7.9 19.2 5 15.6 5 11.5 V6 Z M9 12 L11.2 14.2 L15.2 10"
      {...props}
    />
  );
}

/** Methodology: an open book. */
export function BookIcon(props: IconProps) {
  return (
    <NavIcon
      d="M12 6.5 C10 5 7 4.5 4 5 V18.5 C7 18 10 18.5 12 20 C14 18.5 17 18 20 18.5 V5 C17 4.5 14 5 12 6.5 Z M12 6.5 V20"
      {...props}
    />
  );
}

/** Settings: a slider control. */
export function SlidersIcon(props: IconProps) {
  return (
    <NavIcon d="M4 7 H14 M18 7 H20 M4 17 H6 M10 17 H20 M16 5 V9 M8 15 V19" {...props} />
  );
}

export function RssIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path
        d="M5 11 a8 8 0 0 1 8 8 M5 5 a14 14 0 0 1 14 14"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx="6" cy="18" r="1.5" fill="currentColor" />
    </BaseIcon>
  );
}
